/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Grid, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { FieldValues } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
// import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"; To Do: Un-Comment after the CG-App release
import {
  PROFILES_KEY,
  useAssignProfilesToUser,
  useDeleteProfiles,
  useGetProfiles,
  useGetUsers,
} from "../../../api/profile";
import BackdropLoader from "../../../shared/components/backdrop-loader/BackdropLoader";
import Button from "../../../shared/components/button/Button";
import ConfirmationDialog from "../../../shared/components/confirmation-dialog/ConfirmationDialog";
import { useSnackbar } from "../../../shared/components/snackbar/Snackbar";
import {
  TableMenuItems,
  features,
} from "../../../shared/constants/common.constant";
import {
  downloadPreSignedURL,
  formatDateAsPerServer,
  getItemOnIndex,
  isValidDate,
  formatDate,
  getContactName,
} from "../../../utils";
import type { Profile } from "../types/profile.type";
import AssignProfileConfirmation from "./AssignProfileConfirmation";
import ProfilesListSearch from "./ProfileListSearch";
import ProfileListTable from "./ProfileListTable";
import {
  getInitialFiltersFromUrl,
  buildFilteredUrl,
} from "../profile-utils/ProfilesListFilters";
// import RhfAddEmails from "../../../shared/components/rhf-add-emails/RhfAddEmails";  To Do: once global settings feature came into board
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { getUserGuideDownloadURL } from "../../../api/support/support-api";
import { MLButton } from "../../../shared/styles/common-styles";
import type { ProblemDetails } from "../../../shared/types/problem-details.type";
import type { PaginationQuery } from "../../../shared/types/table.type";
import type { User } from "../../../shared/types/user.type";
import {
  profileActionsErrorTitles,
  profileExportErrorTypes,
  profileSortTypeNames,
} from "../profile.constant";
import type { ProfileListValues } from "./models/ReactHookFormModel";
import { useDispatch } from "react-redux";
import { updateCurrentProfileId } from "../../../store/NotificationsSlice";
import RechargeMetrics from "./RechargeMetrics";
import { useGetRechargeSummary } from "../../../api/recharge";
import { useHasPermission } from "../../../hooks/usePermissions";
import { PermissionCode } from "../../../api/permissions/permissions.types";
import { IsFeatureFlagEnabled } from "../../../store/StoreSelectors";
import type { LocaleLanguages } from "../../../utils";
import CreateProfileModal, {
  CreateProfileModalVariant,
} from "../create-profile-modal/CreateProfileModal";
import type { CreateProfileType } from "../types/createProfile.type";

// To Do: once global settings feature came into board
// const SettingsHeader = styled(Typography)`
//   padding: 10px 0;
// `;

const ShowMetricsButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  height: 3rem;
  margin-bottom: 2rem;
  width: 120px;
  white-space: nowrap;
`;

const DownloadUserGuideButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  height: 3rem;
  margin-bottom: 2rem;
`;

const StyledGrid = styled(Grid)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
`;
// To Do: Un-Comment after the CG-App release
// const StyledSettingsOutlinedIcon = styled(SettingsOutlinedIcon)`
//   margin: 0 2px 2px 0;
// `;

export interface ChipLabelsType {
  key: string;
  value: string;
}

const Profiles: React.FC = () => {
  const [searchParams] = useSearchParams();

  const {
    queryPageNumber,
    queryStatus,
    queryUserNumber,
    queryRiskRating,
    queryReviewBeforeDate,
    queryReviewAfterDate,
    querySearchTerm,
    querySortType,
  } = getInitialFiltersFromUrl(searchParams, profileSortTypeNames.lastModified);

  const localeLanguage = useTranslation().i18n.language as LocaleLanguages;

  const [currentPageNumber, setCurrentPageNumber] = useState(queryPageNumber);
  const [profiles, setProfiles] = useState(Array<Profile>);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedProfiles, setSelectedProfiles] = useState(Array<Profile>);
  const [isIndeterminate, setIsIndeterminate] = useState(false);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentActionToPerform, setCurrentActionToPerform] = useState("");
  const [actionProfile, setActionProfile] = useState<Profile>();
  const [isBulkAction, setIsBulkAction] = useState(false);
  const [searchTerm, setSearchTerm] = useState(querySearchTerm);
  const [users, setUsers] = useState<Array<User>>();
  const [isActionIsInProgress, setIsActionIsInProgress] = useState(false);
  // const [isOpenSettingsPopup, setIsOpenSettingsPopup] = useState(false); To Do: once global settings feature came into board
  // const [emailError, setEmailError] = useState(false); To Do: once global settings feature came into board
  const [status, setStatus] = useState<string[]>(queryStatus);
  const [userNumber, setUserNumber] =
    useState<(number | string)[]>(queryUserNumber);
  const [reviewBeforeDate, setReviewBeforeDate] = useState<string>(
    queryReviewBeforeDate
  );
  const [reviewAfterDate, setReviewAfterDate] =
    useState<string>(queryReviewAfterDate);
  const [riskRating, setRiskRating] = useState<string[]>(queryRiskRating);
  const [sortOrder, setSortOrder] = useState<string>(
    querySortType === profileSortTypeNames.name
      ? profileSortTypeNames.ascending
      : profileSortTypeNames.descending
  );
  const [sortCriteria, setSortCriteria] = useState<string>(
    querySortType === profileSortTypeNames.name
      ? profileSortTypeNames.name
      : profileSortTypeNames.modifiedAt
  );
  const [sortType, setSortType] = useState<string>(querySortType);
  const [isBackDropLoaderOpen, setIsBackDropLoaderOpen] =
    useState<boolean>(false);
  const [isMetricsVisible, setIsMetricsVisible] = useState<boolean>(false);
  const {
    data: rechargeData,
    isError: isRechargeError,
    isLoading: isRechargeLoading,
  } = useGetRechargeSummary(isMetricsVisible);
  // To Do: un-comment after CK-App Release
  // const [perPageSelectedProfilesCount, setPerPageProfilesSelectedCount] =
  //   useState<number>(0);
  // const [displayExportCSV, setDisplayExportCSV] = useState<boolean>(false);

  const isRechargeSummaryFeatureEnabled = IsFeatureFlagEnabled(
    features.rechargeSummary
  );

  const canCreateProfiles = useHasPermission(PermissionCode.CreateProfile);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isCreateProfileModalOpen, setIsCreateProfileModalOpen] =
    useState(false);

  const handleOpenCreateProfileModal = useCallback((): void => {
    setIsCreateProfileModalOpen(true);
  }, []);

  const handleCloseCreateProfileModal = useCallback((): void => {
    setIsCreateProfileModalOpen(false);
  }, []);

  const handleCreateProfileModalSuccess = useCallback(
    async ({
      profileId,
    }: {
      profileId: string;
      profileType: CreateProfileType;
    }): Promise<void> => {
      if (!profileId) {
        return;
      }
      setIsCreateProfileModalOpen(false);
      navigate(`/protect/profile/${profileId}`);
    },
    [navigate]
  );

  const buildUrl = (overrides = {}): string => {
    return buildFilteredUrl(
      {
        status,
        userNumber,
        riskRating,
        reviewBeforeDate,
        reviewAfterDate,
        page: currentPageNumber,
        searchTerm,
        sortType,
      },
      overrides
    );
  };

  useEffect(() => {
    dispatch(updateCurrentProfileId(""));
  }, [dispatch]);

  const { handleSubmit, control, reset } = useForm<ProfileListValues>({
    mode: "all",
    defaultValues: {
      emails: [{ email: "" }],
      searchBoxInput: querySearchTerm,
      isEmailAlertsEnabled: true,
    },
  });

  // const isEmailAlertsEnabled = watch("isEmailAlertsEnabled"); To Do: once global settings feature came into board
  // const selectedEmails = watch("emails");  To Do: once global settings feature came into board

  const { showSnackbar } = useSnackbar();

  const getFormattedDate = (date: string): string | undefined => {
    if (isValidDate(date)) {
      const formatted = formatDateAsPerServer(new Date(date));
      return formatted ?? undefined;
    }
    return undefined;
  };

  const buildPaginationPayload = (): PaginationQuery => {
    const payload: PaginationQuery = {
      page: currentPageNumber,
      limit: 10,
      statuses: status,
      assignees: userNumber,
      riskRatings: riskRating,
      sortBy: sortCriteria,
      searchTerm,
      sortOrder,
    };
    const formattedAfterDate = getFormattedDate(reviewAfterDate);
    if (formattedAfterDate) {
      payload.kycReviewAfter = formattedAfterDate;
    }
    const formattedBeforeDate = getFormattedDate(reviewBeforeDate);
    if (formattedBeforeDate) {
      payload.kycReviewBefore = formattedBeforeDate;
    }
    return payload;
  };

  const payload = useMemo(
    () => buildPaginationPayload(),
    [
      currentPageNumber,
      status,
      userNumber,
      riskRating,
      reviewAfterDate,
      reviewBeforeDate,
      searchTerm,
      sortCriteria,
      sortOrder,
    ]
  );

  const { data, isLoading, isFetching } = useGetProfiles(payload);

  const isProfilesTableLoading =
    isLoading || (isFetching && !isCreateProfileModalOpen);

  const { data: usersData, isLoading: usersDataLoading } = useGetUsers();

  const shouldUpdateUsers = (): boolean => {
    return usersData !== undefined && !usersDataLoading;
  };

  useEffect(() => {
    if (shouldUpdateUsers()) {
      setUsers(usersData);
    }
  }, [data]);

  const queryClient = useQueryClient();

  const deleteProfileMutation = useDeleteProfiles();
  const assignProfilesMutation = useAssignProfilesToUser();

  const { t: translate, i18n } = useTranslation(["profile", "common"]);

  // To Do: once global settings feature came into board
  // const { fields, append, remove } = useFieldArray({
  //   control,
  //   name: "emails",
  // });

  useEffect(() => {
    if (data) {
      setProfiles(
        updateProfileSelection(data?.profiles || [], selectedProfiles)
      );
      setTotalCount(data?.total || 0);
    }
  }, [selectedProfiles, data]);

  useEffect(() => {
    if (!isFetching && isActionIsInProgress) {
      setIsActionIsInProgress(false);
    }
  }, [isFetching]);

  const selectedProfilesPerPageMethod = (): void => {
    let selectedProfilesCountPerPage = 0;
    profiles.forEach((profile) => {
      profile.isSelected && selectedProfilesCountPerPage++;
    });
    // setPerPageProfilesSelectedCount(selectedProfilesCountPerPage); To Do: un-comment after CK-App Release
  };

  const handleHeaderCheckChange = (): void => {
    const copiedProfiles = profiles.map((profile) => {
      profile.isSelected = !isAllChecked;
      return profile;
    });

    if (!isAllChecked) {
      // setDisplayExportCSV(false); To Do: un-comment after CK-App Release
      setIsIndeterminate(false);
    }
    selectedProfilesPerPageMethod();
    setProfiles(copiedProfiles);
    updateSelectedProfilesForHeader();
    setIsAllChecked(!isAllChecked);
  };

  const selectedRiskRating = (riskRating: string[]): void => {
    setRiskRating(riskRating);
    setCurrentPageNumber(1);
    navigate(buildUrl({ riskRating, page: 1 }));
  };

  const selectedStatus = (status: string[]): void => {
    setStatus(status);
    setCurrentPageNumber(1);
    navigate(buildUrl({ status, page: 1 }));
  };

  const selectedAssignee = (userNumber: (number | string)[]): void => {
    setUserNumber(userNumber);
    setCurrentPageNumber(1);
    navigate(buildUrl({ userNumber, page: 1 }));
  };

  const selectedBothDates = (beforeDate: string, afterDate: string): void => {
    setReviewBeforeDate(beforeDate);
    setReviewAfterDate(afterDate);
    setCurrentPageNumber(1);
    navigate(
      buildUrl({
        reviewBeforeDate: beforeDate,
        reviewAfterDate: afterDate,
        page: 1,
      })
    );
  };

  const buildChipLabels = (assignedToValue: string): ChipLabelsType[] => [
    {
      key: "assignedTo",
      value: assignedToValue ? `Assigned To: ${assignedToValue}` : "",
    },
    {
      key: "kycReview",
      value:
        reviewAfterDate && reviewBeforeDate
          ? `KYC Review: ${formatDate(
              reviewAfterDate,
              localeLanguage
            )}-${formatDate(reviewBeforeDate, localeLanguage)}`
          : "",
    },
    {
      key: "kycStatus",
      value:
        status.length > 0
          ? `KYC Status: ${status
              .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
              .join(", ")}`
          : "",
    },
    {
      key: "riskRating",
      value:
        riskRating.length > 0
          ? `Risk Rating: ${riskRating
              .map((risk) => translate("profile:risk.rating.options." + risk))
              .join(", ")}`
          : "",
    },
  ];

  const [chipLabels, setChipLabels] = useState<ChipLabelsType[]>(() =>
    buildChipLabels(userNumber.join(", "))
  );

  useEffect(() => {
    if (userNumber.length === 0) {
      setChipLabels(buildChipLabels(""));
      return;
    }
    if (!users) return;

    const displayNames = userNumber.map((assigneeId) => {
      const matchedUser = users.find(
        (user) => String(user.userId) === String(assigneeId)
      );
      if (!matchedUser) {
        return String(assigneeId);
      }
      const name = getContactName(matchedUser);
      return name === "" ? String(assigneeId) : name;
    });

    setChipLabels(buildChipLabels(displayNames.join(", ")));
  }, [users, userNumber]);

  const updateChipLabels = (
    filteredKey: string,
    filteredValue: string
  ): void => {
    const modifiedChips = chipLabels.map((obj: ChipLabelsType) =>
      obj.key === filteredKey ? { ...obj, value: filteredValue } : obj
    );
    setChipLabels(modifiedChips);
    setCurrentPageNumber(1);
  };

  const updateCheckboxState = (
    selectedCount: number,
    totalCount: number
  ): void => {
    if (selectedCount === 0) {
      setIsAllChecked(false);
      setIsIndeterminate(false);
    } else if (selectedCount === totalCount) {
      setIsAllChecked(true);
      setIsIndeterminate(false);
    } else {
      setIsAllChecked(false);
      setIsIndeterminate(true);
    }
  };

  const updateProfileSelection = (
    profiles: Profile[],
    selectedProfiles: Profile[]
  ): Profile[] => {
    if (!selectedProfiles.length) {
      return profiles;
    }

    let selectedCount = 0;

    profiles.forEach((profile) => {
      const selectedProfile = selectedProfiles.find(
        (item) => item.id === profile.id
      );

      if (selectedProfile) {
        profile.isSelected = true;
        selectedCount++;
      }
    });

    updateCheckboxState(selectedCount, profiles.length);
    return profiles;
  };

  const updateSelectedProfilesForHeader = (): void => {
    const copiedSelectedProfiles = [...selectedProfiles];

    profiles.forEach((profile) => {
      const selectedProfileIndex = copiedSelectedProfiles.findIndex(
        (item) => item.id === profile.id
      );

      if (isAllChecked && selectedProfileIndex > -1) {
        copiedSelectedProfiles.splice(selectedProfileIndex, 1);
      }

      if (!isAllChecked && selectedProfileIndex < 0) {
        copiedSelectedProfiles.push(profile);
      }
    });

    setSelectedProfiles(copiedSelectedProfiles);
  };

  const handleProfileCheckChange = (
    isSelected: boolean,
    profile: Profile
  ): void => {
    if (profiles.length) {
      updateProfilesCheck(isSelected, profile);
      updateSelectedProfiles(profile);
    }
  };

  const updateCheckboxStateFromProfiles = (
    updatedProfiles: Profile[]
  ): void => {
    const hasUnselected = updatedProfiles.some((item) => !item.isSelected);
    if (hasUnselected) {
      const hasSelected = updatedProfiles.some((item) => item.isSelected);
      setIsIndeterminate(hasSelected);
      setIsAllChecked(false);
    } else {
      setIsAllChecked(true);
      setIsIndeterminate(false);
    }
  };

  const updateProfilesCheck = (isSelected: boolean, profile: Profile): void => {
    const copiedProfiles = profiles.map((item) => {
      if (item.id === profile.id) {
        item.isSelected = isSelected;
      }
      return item;
    });
    selectedProfilesPerPageMethod();
    setProfiles(copiedProfiles);
    updateCheckboxStateFromProfiles(copiedProfiles);
  };

  const updateSelectedProfiles = (profile: Profile): void => {
    const selectedProfileIndex = selectedProfiles.findIndex(
      (item) => item.id === profile.id
    );

    const copiedSelectedProfiles = [...selectedProfiles];

    selectedProfileIndex > -1
      ? copiedSelectedProfiles.splice(selectedProfileIndex, 1)
      : copiedSelectedProfiles.push(profile);

    setSelectedProfiles(copiedSelectedProfiles);
  };

  const clearSelectedProfiles = (): void => {
    setSelectedProfiles([]);
    setIsAllChecked(false);
    setIsIndeterminate(false);

    const copiedProfiles = profiles.map((profile) => {
      profile.isSelected = false;
      return profile;
    });

    setProfiles(copiedProfiles);
  };

  const pageChangeHandler = (pageNumber: number): void => {
    setCurrentPageNumber(pageNumber);
    navigate(buildUrl({ page: pageNumber }));
  };

  const getProfileIdsForAction = (): string[] => {
    if (actionProfile && !isBulkAction) {
      return [actionProfile.id];
    }
    return selectedProfiles.map((profile) => profile.id);
  };

  const resetDialogState = (): void => {
    setCurrentActionToPerform("");
    setIsDialogOpen(false);
    setIsBulkAction(false);
    setActionProfile(undefined);
  };

  const handleDeleteDialogClose = (isConfirmed: boolean): void => {
    if (isConfirmed) {
      setIsActionIsInProgress(true);
      const profileIds = getProfileIdsForAction();
      deleteProfiles(profileIds);
    }
    resetDialogState();
  };

  const handleAssignDialogClose = (
    isConfirmed: boolean,
    userId?: number
  ): void => {
    if (isConfirmed) {
      setIsActionIsInProgress(true);
      const profileIds = getProfileIdsForAction();
      assignProfiles(profileIds, userId ?? 0);
    }
    resetDialogState();
  };

  const handleMenuAction = (action: string, actionProfile: Profile): void => {
    setCurrentActionToPerform(action);
    setIsDialogOpen(true);
    setActionProfile(actionProfile);
  };

  const onBulkAction = (action: string): void => {
    if (selectedProfiles.length) {
      setIsBulkAction(true);
      setCurrentActionToPerform(action);
      setIsDialogOpen(true);
    }
  };

  const isProfileLockedError = (error: ProblemDetails): boolean => {
    return (
      error?.details?.type === profileExportErrorTypes.profileConflict &&
      error?.details?.title === profileActionsErrorTitles.profileLocked
    );
  };

  const handleLockedProfileError = (): void => {
    const translationKey = isBulkAction
      ? "common:apiResponse.bulkLockedProfileException"
      : "common:apiResponse.lockedProfileException";
    showSnackbar(
      translate(translationKey, {
        profileName: actionProfile?.name,
      }),
      true
    );
  };

  const handleApiFailed = (error: ProblemDetails): void => {
    setIsActionIsInProgress(false);
    if (isProfileLockedError(error)) {
      handleLockedProfileError();
    } else {
      showUnexpectedError();
    }
  };

  const deleteProfiles = async (profileIds: string[]): Promise<void> => {
    try {
      await deleteProfileMutation.mutateAsync(profileIds);
      onActionSuccess();
      showSnackbar(
        translate("profile:apiResponse.profilesDeleteSuccess").replace(
          "$noOfProfiles",
          profileIds.length.toString()
        )
      );
    } catch (error) {
      handleApiFailed(error as ProblemDetails);
    }
  };

  const assignProfiles = async (
    profileIds: string[],
    userId: number
  ): Promise<void> => {
    try {
      await assignProfilesMutation.mutateAsync({
        profileIds,
        userId,
      });
      onActionSuccess();
      showSnackbar(
        translate("profile:apiResponse.profilesAssignSuccess").replace(
          "$noOfProfiles",
          profileIds.length.toString()
        )
      );
    } catch (error) {
      handleApiFailed(error as ProblemDetails);
    }
  };

  const onActionSuccess = (): void => {
    setSelectedProfiles([]);
    setActionProfile(undefined);

    if (currentPageNumber === 1) {
      queryClient.invalidateQueries(PROFILES_KEY);
    } else {
      setCurrentPageNumber(1);
    }

    navigate(buildUrl({ page: 1 }), { replace: true });

    setIsIndeterminate(false);
    setIsAllChecked(false);
  };

  const handleSearch = (): void => {
    handleSubmit((data) => submitSearch(data))();
  };
  const submitSearch = (data: FieldValues): void => {
    const newSearchTerm = data.searchBoxInput.trim();
    if (searchTerm !== newSearchTerm) {
      setSearchTerm(newSearchTerm);
      setCurrentPageNumber(1);
      navigate(buildUrl({ searchTerm: newSearchTerm, page: 1 }));
    }
  };

  const handleReset = (): void => {
    setSearchTerm("");
    setCurrentPageNumber(1);
    reset({ searchBoxInput: "" });
    navigate(buildUrl({ searchTerm: "", page: 1 }));
  };

  const configureSortForName = (): void => {
    setSortOrder(profileSortTypeNames.ascending);
    setSortCriteria(profileSortTypeNames.name);
  };

  const configureSortForModifiedAt = (): void => {
    setSortOrder(profileSortTypeNames.descending);
    setSortCriteria(profileSortTypeNames.modifiedAt);
  };

  const onChangeProfileSortType = (sortValue: string): void => {
    setSortType(sortValue);
    setCurrentPageNumber(1);
    if (sortValue === profileSortTypeNames.name) {
      configureSortForName();
    } else {
      configureSortForModifiedAt();
    }
    navigate(buildUrl({ sortType: sortValue, page: 1 }));
  };
  // To Do: un-comment after CK-App Release
  // const handleOnClickExportToCSV = () => {
  //   setDisplayExportCSV(true);
  // };

  const onFormSubmit = (): void => {
    handleSearch();
  };

  // To Do: once global settings feature came into board
  // useEffect(() => {
  //   if (isEmailAlertsEnabled && isEmailEntered() >= 1) {
  //     setEmailError(false);
  //   }
  // }, [formState]);

  const showUnexpectedError = (): void => {
    showSnackbar(translate("common:apiResponse.unexpectedErrorOccurred"), true);
  };

  const handleDownloadUserGuide = async (): Promise<void> => {
    setIsBackDropLoaderOpen(true);
    try {
      const language = i18n.language.slice(3, 5);
      const templatePreSignedURL = await getUserGuideDownloadURL(language);
      if (templatePreSignedURL.apiFailed) {
        showUnexpectedError();
      } else {
        await downloadPreSignedURL(templatePreSignedURL.downloadUrl);
      }
    } catch {
      showUnexpectedError();
    } finally {
      setIsBackDropLoaderOpen(false);
    }
  };

  const handleToggleMetrics = useCallback(() => {
    setIsMetricsVisible((prev) => !prev);
  }, []);

  const hasRechargeError = (): boolean => {
    return isRechargeError || rechargeData?.apiFailed === true;
  };

  const shouldShowRechargeError = (): boolean => {
    return isMetricsVisible && !isRechargeLoading && hasRechargeError();
  };

  const isMetricsExpanded = (): boolean => {
    return isMetricsVisible && !isRechargeError && !rechargeData?.apiFailed;
  };

  const getApiErrorStatus = (): boolean => {
    return data?.apiFailed ?? false;
  };

  const isFromSearch = (): boolean => {
    return (searchTerm?.length ?? 0) > 0;
  };

  const getDeleteDialogTitle = (): string => {
    return isBulkAction
      ? translate("deleteProfiles")
      : translate("deleteProfile");
  };

  const getAssignDialogTitle = (): string => {
    return isBulkAction
      ? translate("assignProfiles")
      : translate("assignProfile");
  };

  useEffect(() => {
    if (shouldShowRechargeError()) {
      showSnackbar(
        translate("common:apiResponse.unexpectedErrorOccurred"),
        true
      );
      setIsMetricsVisible(false);
    }
  }, [
    isMetricsVisible,
    isRechargeLoading,
    isRechargeError,
    rechargeData?.apiFailed,
    showSnackbar,
    translate,
  ]);

  return (
      <Box p={2}>
        <BackdropLoader isOpen={isBackDropLoaderOpen} />
        {/* To Do: Un-Comment after the CG-App release */}
        {/* <StyledGrid item xs={6}>
          <StyledButton
            onClick={() => setIsOpenSettingsPopup(!isOpenSettingsPopup)}
            variant="grey"
            aria-label="Settings button"
          >
            <StyledSettingsOutlinedIcon />
            {translate("profile.header.Settings")}
          </StyledButton>
        </StyledGrid> */}
        <StyledGrid item xs={6}>
          {isRechargeSummaryFeatureEnabled && (
            <ShowMetricsButton
              onClick={handleToggleMetrics}
              type="button"
              variant="primary"
              aria-label="show metrics button"
            >
              {isMetricsExpanded() ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
              {isMetricsExpanded()
                ? translate("common:rechargeMetrics.hideMetrics")
                : translate("common:rechargeMetrics.showMetrics")}
            </ShowMetricsButton>
          )}
          <DownloadUserGuideButton
            onClick={() => {
              handleDownloadUserGuide();
            }}
            type="button"
            variant="primary"
            aria-label="download user guide button"
          >
            <DownloadIcon fontSize="small" />
            {translate("common:downloadUserGuide")}
          </DownloadUserGuideButton>
        </StyledGrid>
        {isRechargeSummaryFeatureEnabled && (
          <RechargeMetrics isVisible={isMetricsVisible} />
        )}
        <Typography variant="h5" align="center">
          {translate("profiles.heading")}
        </Typography>
        <form onSubmit={handleSubmit(onFormSubmit)}>
        <ProfilesListSearch
          control={control}
          selectedProfiles={selectedProfiles}
          handleSearch={handleSearch}
          handleReset={handleReset}
          handleClear={clearSelectedProfiles}
          onBulkAction={onBulkAction}
        />
        </form>
        <Grid sx={{ position: "relative" }}>
          <ProfileListTable
            profiles={profiles}
            isIndeterminate={isIndeterminate}
            isAllChecked={isAllChecked}
            totalCount={totalCount}
            currentPageNumber={currentPageNumber}
            isFromSearch={isFromSearch()}
            isApiError={getApiErrorStatus()}
            isFetching={isProfilesTableLoading}
            onProfileCheckChange={handleProfileCheckChange}
            onHeaderCheckChange={handleHeaderCheckChange}
            pageChangeHandler={pageChangeHandler}
            onMenuAction={handleMenuAction}
            activeUsers={users}
            statuses={status}
            selectedStatus={selectedStatus}
            updateChipLabels={updateChipLabels}
            chipLabels={chipLabels}
            userNumbers={userNumber}
            selectedAssignee={selectedAssignee}
            selectedBothDates={selectedBothDates}
            reviewBeforeDate={reviewBeforeDate}
            reviewAfterDate={reviewAfterDate}
            setRiskRating={selectedRiskRating}
            selectedRiskRating={riskRating}
            sortType={sortType}
            onChangeProfileSortType={onChangeProfileSortType}
            searchTerm={searchTerm}
            // perPageSelectedProfilesCount={perPageSelectedProfilesCount} To Do: un-comment after CK-App Release
            // displayExportCSV={displayExportCSV} To Do: un-comment after CK-App Release
            // handleOnClickExportToCSV={handleOnClickExportToCSV} To Do: un-comment after CK-App Release
          />
          {canCreateProfiles && (
            <Grid
              sx={{
                display: "flex",
                py: 2,
                justifyContent: "flex-end",
              }}
            >
              <MLButton
                type="button"
                variant="grey"
                aria-label="create-profile-button"
                onClick={handleOpenCreateProfileModal}
              >
                {translate("profile:profile.create.createManualProfileBtn")}
              </MLButton>
            </Grid>
          )}
        </Grid>
        {currentActionToPerform === TableMenuItems.delete && (
          <ConfirmationDialog
            title={getDeleteDialogTitle()}
            open={isDialogOpen}
            onCancel={() => handleDeleteDialogClose(false)}
            onConfirm={() => handleDeleteDialogClose(true)}
            cancelBtnText={translate("common:cancel")}
            confirmBtnText={translate("common:confirm")}
          >
            {isBulkAction ? (
              <>
                {translate("bulkProfileDeleteConfirmation").replace(
                  "$noOfProfiles",
                  selectedProfiles.length.toString()
                )}
              </>
            ) : (
              <>
                {getItemOnIndex(
                  translate("profileDeleteConfirmation"),
                  "$profile-name",
                  0
                )}
                <strong>{actionProfile?.name}</strong>
                {getItemOnIndex(
                  translate("profileDeleteConfirmation"),
                  "$profile-name",
                  1
                )}
              </>
            )}
          </ConfirmationDialog>
        )}
        {currentActionToPerform === TableMenuItems.assign && (
          <AssignProfileConfirmation
            title={getAssignDialogTitle()}
            open={isDialogOpen}
            onCancel={() => handleAssignDialogClose(false)}
            onConfirm={(userId) => handleAssignDialogClose(true, userId)}
            actionProfile={actionProfile}
            isBulkAssign={isBulkAction}
            selectedProfilesLength={selectedProfiles.length}
          />
        )}
        {/* To Do: once global settings feature came into board */}
        {/* <ConfirmationDialog
          title={translate("profile.header.Settings")}
          open={isOpenSettingsPopup}
          onCancel={() => handleCancel()}
          onConfirm={() => handleEmailSettingsSave()}
          cancelBtnText={translate("common:cancel")}
          confirmBtnText={translate("common:save")}
        >
          <SettingsHeader variant={"body1"} aria-label="settings Header">
            {translate("profile.settings.header")}
          </SettingsHeader>
          <RhfAddEmails
            control={control}
            emailFields={fields}
            append={append}
            remove={remove}
            emailError={emailError}
            receiveEmailAlertsNeeded={false}
            isEmailAlertsEnabled={isEmailAlertsEnabled}
            controlNameCheckbox={"isEmailAlertsEnabled"}
            controlNameEmails={"emails"}
          />
        </ConfirmationDialog> */}

        {isCreateProfileModalOpen && (
          <CreateProfileModal
            isOpen
            onClose={handleCloseCreateProfileModal}
            variant={CreateProfileModalVariant.profilesPage}
            onSuccess={handleCreateProfileModalSuccess}
          />
        )}
        <BackdropLoader isOpen={isActionIsInProgress} />
      </Box>
  );
};

export default Profiles;
